import {HoistModel, managed, TaskObserver, XH} from '@xh/hoist/core';
import {action, bindable, observable} from '@xh/hoist/mobx';
import {Icon} from '@xh/hoist/icon';
import {GridModel} from '@xh/hoist/cmp/grid';
import {asServerException} from '../../../../../core/ServiceUtils';
import {button} from '@xh/hoist/kit/blueprint';
import classNames from 'classnames';
import {Roles, Security} from '../../../../data/DataTypes';
import {secExchangeBadge} from '../../../../cmp/secBadges/SecBadges';

export class SecurityImportDialogModel extends HoistModel {
    importTask = TaskObserver.trackLast({message: 'Importing...'});
    loadTask = TaskObserver.trackLast();
    @observable accessor isOpen = false;
    @managed gridModel = this.createGridModel();
    @bindable accessor searchString;
    private resolver: (boolean) => void;
    private hasImports: boolean;

    constructor() {
        super();

        this.addReaction({
            track: () => this.searchString,
            run: () => this.loadAsync().linkTo(this.loadTask),
            debounce: {interval: 300}
        });
    }

    @action
    openAsync(): Promise<boolean> {
        this.isOpen = true;
        this.searchString = null;
        this.hasImports = false;
        this.gridModel.clear();
        this.gridModel.clearSelection();
        return new Promise(r => (this.resolver = r));
    }

    @action
    close() {
        this.isOpen = false;
        this.resolver(this.hasImports);
    }

    override async doLoadAsync() {
        this.gridModel.clear();
        try {
            const securities = await XH.secMasterService.searchSecurityUniverseAsync(this.searchString);
            this.gridModel.loadData(securities.map(s => ({...s, isExisting: s.id > 0})));
        } catch (e) {
            XH.handleException(asServerException(e), {title: 'Error loading securities'});
        }
    }

    async importSecurity(secStub: Security) {
        if (!XH.entitlementService.hasRole(Roles.SEC_MASTER_MANAGER)) {
            XH.dangerToast('You do not have permission to import securities.');
            return;
        }

        const confirm = await XH.confirm({
            message: `Import ${secStub.ticker} (${secStub.name})?`,
            icon: Icon.questionCircle(),
            title: 'User confirmation required'
        });
        if (!confirm) return;

        await XH.secMasterService.importSecurityAsync(secStub).linkTo(this.importTask);
        this.hasImports = true;
    }

    private createGridModel(): GridModel {
        return new GridModel({
            emptyText: 'No data',
            sortBy: 'ticker',
            store: {
                idSpec: () => XH.genId()
            },
            columns: [
                {
                    field: 'action',
                    headerName: null,
                    cellClass: 'xh-action-col-cell',
                    width: 45,
                    align: 'center',
                    sortable: false,
                    resizable: false,
                    filterable: false,
                    excludeFromExport: true,
                    renderer: (value, {record, column}) => {
                        if (record.raw.isExisting) return Icon.check();
                        else
                            return button({
                                icon: Icon.plus({intent: 'success'}),
                                className: classNames('bp6-small', 'xh-record-action-button'),
                                onClick: e => {
                                    e.stopPropagation();
                                    this.importSecurity(record.raw as Security);
                                }
                            });
                    }
                },
                {
                    field: 'type',
                    width: 100
                },
                {
                    field: 'ticker',
                    width: 110
                },
                {
                    field: 'name',
                    flex: 1
                },
                {
                    field: 'exchange',
                    width: 100,
                    renderer: v => secExchangeBadge(v)
                }
            ]
        });
    }
}
